import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useJournals, useUpdateJournal, useDeleteJournal } from "@/hooks/useJournals";
import { supabase } from "@/integrations/supabase/client";
import { devLogger } from "@/lib/dev-logger";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IconHome, IconMessage, IconCompass, IconWorld, IconSettings, IconPlus, IconLayoutSidebarLeftCollapse, IconSearch, IconLayoutSidebar, IconSmartHome, IconBrandSafari, IconChevronDown, IconUser, IconHelp, IconLogout, IconExternalLink, IconHelpCircle } from "@tabler/icons-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import ProfilePopup from "./ProfilePopup";
import SidebarCreditDisplay from "./subscription/SidebarCreditDisplay";
import SidebarHeader from "./sidebar/SidebarHeader";
import SidebarCollapsed from "./sidebar/SidebarCollapsed";
import SidebarJournals from "./sidebar/SidebarJournals";
import SearchModal from "./search/SearchModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface SidebarProps {
  onToggle?: (collapsed: boolean) => void;
  showHeader?: boolean;
  onClose?: () => void;
  collapsed?: boolean;
}

const Sidebar = ({
  onToggle,
  showHeader = false,
  onClose,
  collapsed = false
}: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const activeJournalId = currentPath.startsWith('/journals/') ? currentPath.split('/')[2] : undefined;
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Remove internal state and use prop instead
  const isCollapsed = collapsed;
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedJournalId, setSelectedJournalId] = useState<string | null>(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [userProfile, setUserProfile] = useState<{
    full_name?: string | null;
    avatar_url?: string | null;
  }>({});

  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Use journals hook for data fetching
  const { data: journals = [], isLoading: journalsLoading } = useJournals();
  const updateJournal = useUpdateJournal();
  const deleteJournal = useDeleteJournal();

  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('full_name, avatar_url')
          .eq('id', user.id)
          .maybeSingle();
        if (!error && profile) {
          setUserProfile({
            full_name: profile.full_name,
            avatar_url: profile.avatar_url
          });
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchUserProfile();
  }, [user]);

  // Set up scroll event cleanup
  useEffect(() => {
    return () => {
      if (window.scrollTimeout) {
        clearTimeout(window.scrollTimeout);
      }
    };
  }, []);

  // Set up realtime subscriptions for journal updates
  useEffect(() => {
    if (!user) return;
    const journalChannel = supabase.channel('journal-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'journals',
        filter: `user_id=eq.${user.id}`
      }, payload => {
        devLogger.log('Journal change:', payload);
        queryClient.invalidateQueries({ queryKey: ['journals', user.id] });
        if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
          queryClient.invalidateQueries({ queryKey: ['journal', payload.new?.id] });
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(journalChannel);
    };
  }, [user, queryClient]);

  // Filter journals based on search query - Journal type uses 'name' not 'title'
  const filteredJournals = journals.filter(journal => {
    const journalName = (journal as any).name || (journal as any).title || '';
    return journalName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleHomeClick = () => {
    navigate("/");
    onClose?.();
  };

  const handleExploreClick = () => {
    navigate("/community");
    onClose?.();
  };

  const handleJournalClick = (journalId: string) => {
    navigate(`/journals/${journalId}`);
    // Don't close sidebar when clicking journals - preserve sidebar state
  };

  const handleRenameJournal = (journalId: string) => {
    const journal = journals.find(j => j.id === journalId);
    setSelectedJournalId(journalId);
    setRenameTitle(journal?.name || (journal as any)?.title || '');
    setIsRenameModalOpen(true);
  };

  const handleDeleteJournal = (journalId: string) => {
    setSelectedJournalId(journalId);
    setIsDeleteModalOpen(true);
  };

  const handleRename = async () => {
    if (!selectedJournalId || !renameTitle.trim()) return;
    try {
      await updateJournal.mutateAsync({
        journalId: selectedJournalId,
        updates: { name: renameTitle.trim() }
      });
      setIsRenameModalOpen(false);
      setSelectedJournalId(null);
      setRenameTitle("");
    } catch (error) {
      console.error('Error renaming journal:', error);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedJournalId || !user) return;
    try {
      await deleteJournal.mutateAsync(selectedJournalId);
      setIsDeleteModalOpen(false);
      setSelectedJournalId(null);

      // Navigate to home if we're currently viewing the deleted journal
      if (activeJournalId === selectedJournalId) {
        navigate('/');
        onClose?.();
      }
    } catch (error) {
      console.error('Error deleting journal:', error);
    }
  };

  const toggleSidebar = () => {
    const newCollapsed = !isCollapsed;
    onToggle?.(newCollapsed);
  };

  const handleExpand = () => {
    onToggle?.(false);
  };

  const selectedJournal = journals.find(j => j.id === selectedJournalId);

  return (
    <div className={`h-full flex flex-col transition-all duration-700 ease-out ${isCollapsed ? 'w-auto' : 'w-68'}`} style={{ backgroundColor: '#EBEBEB' }}>
      {/* Logo Header with collapse button when opened */}
      {!isCollapsed && (
        <div className="p-4 flex items-center justify-between py-[16px]">
          <img src="/Logo/sblogo.png" alt="Studibudi" className="h-8 w-auto" />
          <div onClick={toggleSidebar} className="cursor-pointer p-2 rounded-lg">
            <IconLayoutSidebar className="w-6 h-6 text-gray-600" stroke={1.6} />
          </div>
        </div>
      )}

      {showHeader && <SidebarHeader isCollapsed={isCollapsed} onToggle={toggleSidebar} onHomeClick={handleHomeClick} showCloseButton={!!onClose} onClose={onClose} />}

      {!isCollapsed ? (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Main Navigation */}
          <div className="space-y-1 px-4 mb-3 pb-3 border-b border-gray-300 flex-shrink-0">
            <div
              onClick={() => {
                navigate('/');
                onClose?.();
              }}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer ${currentPath === '/' || currentPath === '/journals' ? "bg-white" : "hover:bg-white/50"}`}
            >
              <IconSmartHome className="w-5 h-5 text-foreground/70" stroke={1.6} />
              <span className="text-sm font-normal text-foreground">Home</span>
            </div>

            <div
              onClick={() => setIsSearchModalOpen(true)}
              className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer hover:bg-white/50"
            >
              <IconSearch className="w-5 h-5 text-foreground/70" stroke={1.6} />
              <span className="text-sm font-normal text-foreground">Search</span>
            </div>

            <div
              onClick={() => {
                navigate('/community');
                onClose?.();
              }}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer ${currentPath === '/community' ? "bg-white" : "hover:bg-white/50"}`}
            >
              <IconBrandSafari className="w-5 h-5 text-foreground/70" stroke={1.6} />
              <span className="text-sm font-normal text-foreground">Community</span>
            </div>
          </div>

          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="flex-1 min-h-0 overflow-hidden">
              <div
                ref={scrollAreaRef}
                className={`h-full px-3 overflow-y-auto ${isScrolling ? 'scrollbar-thin-light' : 'scrollbar-hide'}`}
                onScroll={() => {
                  setIsScrolling(true);
                  if (window.scrollTimeout) {
                    clearTimeout(window.scrollTimeout);
                  }
                  window.scrollTimeout = setTimeout(() => setIsScrolling(false), 1000);
                }}
              >
                <SidebarJournals
                  journals={filteredJournals}
                  searchQuery={searchQuery}
                  onJournalClick={handleJournalClick}
                  onJournalRename={handleRenameJournal}
                  onJournalDelete={handleDeleteJournal}
                  activeJournalId={activeJournalId}
                  loading={journalsLoading}
                />
              </div>
            </div>
          </div>

          {/* Fixed Footer Section - User Avatar and Credits */}
          <div className="bg-[#EBEBEB] flex-shrink-0 mt-auto px-[12px] py-[12px]">
            {/* Credits Section */}
            <SidebarCreditDisplay className="mx-[24px] rounded-bl-[0px] rounded-br-[0px]" />

            {/* User Profile */}
            <Popover>
              <PopoverTrigger asChild>
                <div className="flex items-center gap-3 cursor-pointer bg-white hover:bg-gray-50 transition-colors px-[8px] py-[8px] rounded-2xl w-full border border-gray-300">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={userProfile.avatar_url || undefined} alt={userProfile.full_name || "User"} />
                    <AvatarFallback className="bg-gray-300 text-gray-700 text-sm">
                      {userProfile.full_name ? userProfile.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : user?.email?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {userProfile.full_name || user?.email?.split('@')[0] || "User"}
                    </p>
                  </div>
                  <IconChevronDown className="w-4 h-4 text-foreground/70" stroke={1.6} />
                </div>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="top"
                className="w-full bg-white border border-gray-200 shadow-lg rounded-2xl z-[100]"
                sideOffset={8}
                style={{ width: 'calc(286px - 24px)' }}
              >
                <div className="py-1 px-3 space-y-1">
                  {/* User Info Header */}
                  <div className="flex items-center gap-3 py-2">
                    <Avatar className="w-12 h-12 rounded-2xl">
                      <AvatarImage src={userProfile.avatar_url || undefined} alt={userProfile.full_name || "User"} />
                      <AvatarFallback className="bg-[#EDEDEB] text-foreground text-sm font-medium border border-[#DADAD9] rounded-2xl">
                        {userProfile.full_name ? userProfile.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : user?.email?.charAt(0).toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground truncate">
                        {userProfile.full_name || user?.email?.split('@')[0] || "User"}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {user?.email}
                      </p>
                    </div>
                  </div>

                  {/* Credit Details Section */}
                  <div className="px-3 py-2 space-y-1.5 border rounded-2xl" style={{ backgroundColor: '#F7F7F7', borderColor: '#E9E9E9' }}>
                    {/* Free/Upgrade Row */}
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold text-foreground">Free</span>
                      <button className="px-4 py-1.5 bg-foreground text-primary-foreground text-sm font-medium rounded-full hover:bg-foreground/90 transition-colors">
                        Upgrade
                      </button>
                    </div>

                    {/* Dashed Separator */}
                    <div className="border-t border-dashed border-gray-300" />

                    {/* Credits Row */}
                    <button className="w-full flex items-center justify-between py-2 hover:bg-gray-50 rounded-lg transition-colors">
                      <div className="flex items-center gap-2">
                        <img src="/lovable-uploads/3dc23cd9-8051-4e9c-b14f-9d3ee05ae883.png" alt="Credits" className="h-5 w-auto" />
                        <span className="text-sm font-medium text-foreground">Credits</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">1,300</span>
                        <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  </div>

                  {/* Menu Items */}
                  <ProfilePopup>
                    <button className="w-full flex items-center gap-3 px-3 py-2 rounded-2xl hover:bg-gray-50 transition-colors text-left">
                      <IconUser className="w-5 h-5 text-foreground/70" stroke={1.6} />
                      <span className="text-sm font-medium text-foreground">Account</span>
                    </button>
                  </ProfilePopup>

                  <Separator className="my-2" />

                  <button
                    onClick={() => window.open('https://studibudi.ai/', '_blank')}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-2xl hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <IconHome className="w-5 h-5 text-foreground/70" stroke={1.6} />
                      <span className="text-sm font-medium text-foreground">Homepage</span>
                    </div>
                    <IconExternalLink className="w-4 h-4 text-muted-foreground" stroke={1.6} />
                  </button>

                  <button
                    onClick={() => window.open('mailto:support@studibudi.com', '_blank')}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-2xl hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <IconHelpCircle className="w-5 h-5 text-foreground/70" stroke={1.6} />
                      <span className="text-sm font-medium text-foreground">Contact us</span>
                    </div>
                    <IconExternalLink className="w-4 h-4 text-muted-foreground" stroke={1.6} />
                  </button>

                  <Separator className="my-2" />

                  <button
                    onClick={async () => {
                      await signOut();
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-2xl hover:bg-gray-50 transition-colors text-left"
                  >
                    <IconLogout className="w-5 h-5 text-red-500" stroke={1.6} />
                    <span className="text-sm font-medium text-red-500">Sign out</span>
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col">
          <SidebarCollapsed
            onExpand={handleExpand}
            onHomeClick={handleHomeClick}
            onSearchClick={() => setIsSearchModalOpen(true)}
            onExploreClick={handleExploreClick}
            onNewTopicClick={() => {
              navigate('/journals');
              onClose?.();
            }}
          />
        </div>
      )}

      <SearchModal isOpen={isSearchModalOpen} onOpenChange={setIsSearchModalOpen} onClose={onClose} />

      {/* Rename Journal Modal */}
      <Dialog open={isRenameModalOpen} onOpenChange={setIsRenameModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Rename Journal</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={renameTitle}
              onChange={(e) => setRenameTitle(e.target.value)}
              placeholder="Journal title"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRename();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRenameModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={!renameTitle.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Journal Confirmation */}
      <AlertDialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Journal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedJournal?.name || (selectedJournal as any)?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Sidebar;